import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaCircleExclamation, FaMagnifyingGlass, FaTriangleExclamation } from "react-icons/fa6";
import { findBanquetByGooglePlaceId, quickAddBanquetConfirm } from "../services/banquetOwnerBanquetService.js";
import { hasGoogleRedirectPattern, importGooglePlaceDetails, resolveGooglePhotoPreviewUrls } from "../services/googlePlacesImportService.js";
import { logoutBanquetOwner } from "../services/banquetOwnerAuthService.js";
import GenerateOwnerLinkButton from "../components/GenerateOwnerLinkButton.jsx";

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";

const STEPS = {
  IDLE: "idle",
  SEARCHING: "searching",
  DUPLICATE: "duplicate",
  PREVIEW: "preview",
  CONFIRMING: "confirming",
  SUCCESS: "success",
};

export default function SuperAdminQuickAddBanquetPage() {
  const [input, setInput] = useState("");
  const [step, setStep] = useState(STEPS.IDLE);
  const [error, setError] = useState("");
  const [resolvedPlace, setResolvedPlace] = useState(null);
  const [previewPhotos, setPreviewPhotos] = useState([]);
  const [duplicateBanquet, setDuplicateBanquet] = useState(null);
  const [result, setResult] = useState(null);

  const resetToIdle = () => {
    setStep(STEPS.IDLE);
    setError("");
    setResolvedPlace(null);
    setPreviewPhotos([]);
    setDuplicateBanquet(null);
    setResult(null);
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;

    setError("");
    setDuplicateBanquet(null);
    setResult(null);
    setStep(STEPS.SEARCHING);

    try {
      const looksLikeUrl = /^https?:\/\//i.test(trimmed) || hasGoogleRedirectPattern(trimmed);
      const place = await importGooglePlaceDetails(looksLikeUrl ? "" : trimmed, "", looksLikeUrl ? trimmed : "");

      if (!place.googlePlaceId) {
        throw new Error("Could not resolve a Google Place ID for this listing. Try a more specific name or a direct Google Maps link.");
      }

      const existing = await findBanquetByGooglePlaceId({ googlePlaceId: place.googlePlaceId });
      if (existing) {
        setDuplicateBanquet(existing);
        setResolvedPlace(place);
        setStep(STEPS.DUPLICATE);
        return;
      }

      const photos = await resolveGooglePhotoPreviewUrls(place.googlePhotos, { maxCount: 4 });
      setResolvedPlace(place);
      setPreviewPhotos(photos);
      setStep(STEPS.PREVIEW);
    } catch (searchError) {
      setError(searchError.message || "Could not import details. Please try again.");
      setStep(STEPS.IDLE);
    }
  };

  const handleConfirm = async (createForOwnerFill = false) => {
    if (!resolvedPlace?.googlePlaceId) return;
    setError("");
    setStep(STEPS.CONFIRMING);

    try {
      const response = await quickAddBanquetConfirm({ placeId: resolvedPlace.googlePlaceId, createForOwnerFill });
      setResult(response);
      setStep(STEPS.SUCCESS);
    } catch (confirmError) {
      setError(confirmError.message || "Unable to publish this banquet. Please try again.");
      setStep(STEPS.PREVIEW);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutBanquetOwner();
    } catch (logoutError) {
      setError(logoutError.message || "Unable to logout.");
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f5f7f4] px-3 py-6 sm:px-4 sm:py-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <Link to="/super-admin/banquet-owners" className="hover:text-emerald-600">
                Super Admin
              </Link>
              <span>/</span>
              <span className="text-slate-900">Quick Add Banquet</span>
            </div>
            <h1 className="mt-3 text-2xl font-semibold text-slate-900 sm:text-3xl">Quick Add Banquet</h1>
            <p className="mt-2 text-sm text-slate-600">
              Publish a new banquet straight from Google Maps — no owner account or credentials needed. Link it to a real owner later from Super Admin Banquet Owners.
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="min-h-[44px] rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Logout
          </button>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <form onSubmit={handleSearch} className="flex flex-col gap-3 sm:flex-row">
            <input
              className={inputClassName}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Banquet name, or a Google Maps link"
              disabled={step === STEPS.SEARCHING || step === STEPS.CONFIRMING}
            />
            <button
              type="submit"
              disabled={!input.trim() || step === STEPS.SEARCHING || step === STEPS.CONFIRMING}
              className="inline-flex min-h-[46px] shrink-0 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaMagnifyingGlass />
              {step === STEPS.SEARCHING ? "Searching..." : "Search"}
            </button>
          </form>

          {error ? (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700">
              <FaCircleExclamation className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {step === STEPS.DUPLICATE && duplicateBanquet ? (
            <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <FaTriangleExclamation className="mt-0.5 shrink-0 text-amber-600" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-900">
                  This banquet already exists as "{duplicateBanquet.name || duplicateBanquet.title}" — view it instead of creating a duplicate?
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={`/banquets/${duplicateBanquet.slug || duplicateBanquet.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-[38px] items-center rounded-xl bg-amber-600 px-4 py-2 text-xs font-bold text-white hover:bg-amber-700"
                  >
                    View existing listing
                  </a>
                  <button
                    type="button"
                    onClick={resetToIdle}
                    className="min-h-[38px] rounded-xl border border-amber-300 bg-white px-4 py-2 text-xs font-bold text-amber-900"
                  >
                    Search something else
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {step === STEPS.PREVIEW || step === STEPS.CONFIRMING ? (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">Preview — not saved yet</p>
              <h2 className="mt-2 text-xl font-black text-slate-950">{resolvedPlace?.name}</h2>
              <p className="mt-1 text-sm text-slate-600">{resolvedPlace?.address}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm font-semibold text-slate-700">
                <span>
                  &#9733; {resolvedPlace?.googleRating || "No rating"}
                  {resolvedPlace?.googleRatingsTotal ? ` (${resolvedPlace.googleRatingsTotal} reviews)` : ""}
                </span>
                {resolvedPlace?.city ? <span className="rounded-full bg-white px-3 py-1 ring-1 ring-slate-200">{resolvedPlace.city}</span> : null}
              </div>
              {previewPhotos.length ? (
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {previewPhotos.map((url) => (
                    <img key={url} src={url} alt={resolvedPlace?.name} className="aspect-square w-full rounded-xl object-cover" />
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-xs font-semibold text-slate-500">No photos available on this Google listing.</p>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleConfirm(false)}
                  disabled={step === STEPS.CONFIRMING}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaCheck />
                  {step === STEPS.CONFIRMING ? "Publishing..." : "Confirm & Publish"}
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirm(true)}
                  disabled={step === STEPS.CONFIRMING}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl border border-emerald-600 bg-white px-5 py-3 text-sm font-bold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {step === STEPS.CONFIRMING ? "Creating..." : "Create for Owner to Fill"}
                </button>
                <button
                  type="button"
                  onClick={resetToIdle}
                  disabled={step === STEPS.CONFIRMING}
                  className="min-h-[44px] rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
              <p className="mt-3 text-xs font-semibold text-slate-500">
                "Confirm & Publish" goes live immediately with no owner assigned. "Create for Owner to Fill" instead saves it privately and lets you send the owner a link to fill in pricing, capacity, and the rest themselves — nothing goes live until you approve their submission.
              </p>
            </div>
          ) : null}

          {step === STEPS.SUCCESS && result && result.mode === "owner_fill" ? (
            <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <p className="flex items-center gap-2 text-sm font-black text-emerald-800">
                <FaCheck /> Created "{result.name}" — awaiting owner details.
              </p>
              <p className="mt-1 text-xs font-semibold text-emerald-700">
                {result.photosUploaded}/{result.photosRequested} photo(s) already fetched from Google. Not live yet — generate a link below and send it to the owner on WhatsApp.
              </p>
              <div className="mt-4">
                <GenerateOwnerLinkButton banquetId={result.id} />
              </div>
              <button
                type="button"
                onClick={() => {
                  resetToIdle();
                  setInput("");
                }}
                className="mt-4 min-h-[40px] rounded-xl border border-emerald-300 bg-white px-4 py-2 text-xs font-bold text-emerald-900"
              >
                Add another banquet
              </button>
            </div>
          ) : null}

          {step === STEPS.SUCCESS && result && result.mode !== "owner_fill" ? (
            <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <p className="flex items-center gap-2 text-sm font-black text-emerald-800">
                <FaCheck /> Published "{result.name}" successfully.
              </p>
              <p className="mt-1 text-xs font-semibold text-emerald-700">
                {result.photosUploaded}/{result.photosRequested} photo(s) uploaded.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={`/banquets/${result.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[40px] items-center rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700"
                >
                  View live listing
                </a>
                <button
                  type="button"
                  onClick={() => {
                    resetToIdle();
                    setInput("");
                  }}
                  className="min-h-[40px] rounded-xl border border-emerald-300 bg-white px-4 py-2 text-xs font-bold text-emerald-900"
                >
                  Add another banquet
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
